import type {
  ConsentKitConfig,
  ConsentRecord,
  Decision,
  LocaleCode,
  ResolvedRegion,
} from "../types";
import { RECORD_VERSION } from "./defaults";
import { nativeFetch } from "./native";

/**
 * Art. 7 Abs. 1 DSGVO puts the burden of proof on the controller: you must be able to
 * demonstrate that the user consented. The record captures what was decided, when, on
 * which version of the banner, and by which deliberate action.
 *
 * The US regime needs the same file to prove the opposite thing — that an opt-out was
 * received and acted on, and that a GPC signal was not ignored — so `region`, `regime` and
 * `gpc` travel with every record. Without them a stored `{marketing: true}` is ambiguous:
 * granted consent, or merely an objection nobody raised.
 */
export function createRecord(
  config: ConsentKitConfig,
  decision: Decision,
  method: ConsentRecord["method"],
  locale: LocaleCode,
  region: ResolvedRegion,
): ConsentRecord {
  return {
    id: randomId(),
    timestamp: new Date().toISOString(),
    decision: { ...decision },
    vendors: [...config.vendors],
    configVersion: config.configVersion,
    recordVersion: RECORD_VERSION,
    locale,
    region: region.region,
    regime: region.regime,
    gpc: region.gpc,
    method,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
}

/**
 * Optional server-side copy. Fire-and-forget with `keepalive` so a decision made right
 * before a navigation still lands. Failure is silent: a network problem must never block
 * the user's choice from taking effect locally.
 */
export function uploadRecord(config: ConsentKitConfig, record: ConsentRecord): void {
  const endpoint = config.recordEndpoint;
  if (!endpoint || !nativeFetch) return;

  void nativeFetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(record),
    keepalive: true,
    credentials: "omit",
  }).catch(() => {
    /* proof upload is best-effort; the local record remains authoritative */
  });
}

function randomId(): string {
  const globalCrypto = typeof crypto !== "undefined" ? crypto : undefined;
  if (globalCrypto && typeof globalCrypto.randomUUID === "function") {
    return globalCrypto.randomUUID();
  }
  if (globalCrypto && typeof globalCrypto.getRandomValues === "function") {
    const bytes = globalCrypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
